import type { DriverOptionAssignment, DriverRecord, DriverTariffAssignment } from './drivers.store';
import type { OrderRecord } from './orders.store';
import type { OptionRecord, RegionRecord, TariffRecord } from './reference-data.store';

export type CountryCode = 'AM' | 'GE' | 'RU';

export interface MapViewport {
  lat: number;
  lng: number;
  zoom: number;
}

export interface CountryDefinition {
  code: CountryCode;
  dialCode: string;
  phoneLength: number;
  mapCenter: MapViewport;
}

export interface CarTypeRecord {
  id: number;
  name: string;
  tariffIds: number[];
}

export interface DriverTariffRegionGroup {
  regionId: number | null;
  tariffs: DriverTariffAssignment[];
}

export interface EnrichedDriverRecord extends DriverRecord {
  airConditioning: boolean;
  regionIds: number[];
  activeRegionIds: number[];
  carTypeIds: number[];
  activeCarTypeIds: number[];
  primaryCarTypeId: number | null;
  isTruck: boolean;
  isManipulator: boolean;
  tariffRegionPairs: DriverTariffRegionGroup[];
  optionNames: string[];
  activeOptionNames: string[];
}

export const NO_FILTER_SELECTION_ID = -1;
const BREAD_TARIFF_NAMES = new Set<string>(['ggrustic', 'ggbaguette']);
const ARA_TARIFF_NAMES = new Set<string>(['ggshuttle ggara', 'ggara']);
const TRUCK_TARIFF_NAME_PREFIX = 'ggtruck';
const MANIPULATOR_TARIFF_NAME_PREFIX = 'ggmanipulator';
const SHUTTLE_TARIFF_NAME_PREFIX = 'ggshuttle';
const SHUTTLE_EXCLUDED_NAME_SUBSTRINGS = ['airport'];

export const SHUTTLE_TYPE_FILTER_ID = -2;

export const LEGACY_COUNTRY_DEFINITIONS: CountryDefinition[] = [
  {
    code: 'AM',
    dialCode: '+374',
    phoneLength: 8,
    mapCenter: {
      lat: 40.187203,
      lng: 44.515207,
      zoom: 13
    }
  },
  {
    code: 'GE',
    dialCode: '+995',
    phoneLength: 9,
    mapCenter: {
      lat: 41.729581,
      lng: 44.810893,
      zoom: 13
    }
  },
  {
    code: 'RU',
    dialCode: '+7',
    phoneLength: 10,
    mapCenter: {
      lat: 55.753805,
      lng: 37.621246,
      zoom: 11
    }
  }
];

export function normalizeLegacyRegionSelectionIds(rawSelections: unknown): number[] {
  if (!Array.isArray(rawSelections)) {
    return [];
  }

  return rawSelections
    .map((selection) => readNumber(asRecord(selection), 'regionId'))
    .filter(isNumber);
}

export function normalizeLegacyCarTypeSelectionIds(rawSelections: unknown): number[] {
  if (!Array.isArray(rawSelections)) {
    return [];
  }

  return rawSelections
    .map((selection) => readNumber(asRecord(selection), 'typeId'))
    .filter((value): value is number => isNumber(value) && value !== 0);
}

export function buildCarTypesFromTariffs(tariffs: TariffRecord[]): CarTypeRecord[] {
  const byId = new Map<number, CarTypeRecord>();

  byId.set(0, {
    id: 0,
    name: 'None',
    tariffIds: []
  });

  for (const tariff of tariffs) {
    if (!isNumber(tariff.typeId)) {
      continue;
    }

    const existing = byId.get(tariff.typeId);

    if (existing) {
      if (!existing.tariffIds.includes(tariff.id)) {
        existing.tariffIds.push(tariff.id);
      }
      continue;
    }

    byId.set(tariff.typeId, {
      id: tariff.typeId,
      name: tariff.name,
      tariffIds: [tariff.id]
    });
  }

  // Two types can have tariffs with the same name (e.g. "ggTruck" for both ggTruck M and L).
  // Only for those, fall back to the car type's own name so they can be told apart.
  const typeNames = new Map<number, string>();
  for (const tariff of tariffs) {
    if (isNumber(tariff.typeId) && tariff.typeName && !typeNames.has(tariff.typeId)) {
      typeNames.set(tariff.typeId, tariff.typeName);
    }
  }
  const nameCounts = new Map<string, number>();
  for (const carType of byId.values()) {
    nameCounts.set(carType.name, (nameCounts.get(carType.name) ?? 0) + 1);
  }
  for (const carType of byId.values()) {
    const typeName = typeNames.get(carType.id);
    if ((nameCounts.get(carType.name) ?? 0) > 1 && typeName) {
      carType.name = typeName;
    }
  }

  return [...byId.values()].sort((left, right) => left.id - right.id);
}

export function buildLookupMap<T extends { id: number }>(records: T[]): Record<number, T> {
  return records.reduce<Record<number, T>>((lookup, record) => {
    lookup[record.id] = record;
    return lookup;
  }, {});
}

export function deriveBreadTariffIds(tariffs: TariffRecord[]): number[] {
  return tariffs
    .filter((tariff) => BREAD_TARIFF_NAMES.has(normalizeTariffName(tariff.name)))
    .map((tariff) => tariff.id);
}

export function deriveTruckTariffIds(tariffs: TariffRecord[]): number[] {
  return tariffs
    .filter((tariff) => normalizeTariffName(tariff.name).startsWith(TRUCK_TARIFF_NAME_PREFIX))
    .map((tariff) => tariff.id);
}

export function deriveAraTariffIds(tariffs: TariffRecord[]): number[] {
  return tariffs
    .filter((tariff) => ARA_TARIFF_NAMES.has(normalizeTariffName(tariff.name)))
    .map((tariff) => tariff.id);
}

export function deriveShuttleTariffIds(tariffs: TariffRecord[]): number[] {
  return tariffs
    .filter((tariff) => {
      const normalized = normalizeTariffName(tariff.name);
      return (
        normalized.startsWith(SHUTTLE_TARIFF_NAME_PREFIX) &&
        !ARA_TARIFF_NAMES.has(normalized) &&
        !SHUTTLE_EXCLUDED_NAME_SUBSTRINGS.some((substring) => normalized.includes(substring))
      );
    })
    .map((tariff) => tariff.id);
}

export function compareRegionsForFilter(left: RegionRecord, right: RegionRecord): number {
  const leftIsYerevan = isYerevanRegion(left);
  const rightIsYerevan = isYerevanRegion(right);

  if (leftIsYerevan !== rightIsYerevan) {
    return leftIsYerevan ? -1 : 1;
  }

  return left.name.localeCompare(right.name);
}

export function deriveSelectedTariffIds(
  tariffs: TariffRecord[],
  selectedRegionIds: number[],
  selectedCarTypeIds: number[]
): number[] {
  const effectiveRegionIds = hasNoFilterSelection(selectedRegionIds) ? [] : selectedRegionIds;
  const effectiveSelectedCarTypeIds = hasNoFilterSelection(selectedCarTypeIds)
    ? []
    : selectedCarTypeIds.filter((carTypeId) => carTypeId !== 0);

  if (effectiveRegionIds.length === 0 && effectiveSelectedCarTypeIds.length === 0) {
    return [NO_FILTER_SELECTION_ID];
  }

  return tariffs
    .filter((tariff) => {
      const matchesRegion =
        effectiveRegionIds.length === 0 ||
        !isNumber(tariff.regionId) ||
        effectiveRegionIds.includes(tariff.regionId);
      const matchesCarType =
        effectiveSelectedCarTypeIds.length === 0 ||
        (isNumber(tariff.typeId) && effectiveSelectedCarTypeIds.includes(tariff.typeId));

      return matchesRegion && matchesCarType;
    })
    .map((tariff) => tariff.id);
}

export function deriveEffectiveRegionIds(regions: RegionRecord[], selectedRegionIds: number[]): number[] {
  if (!hasNoFilterSelection(selectedRegionIds) && selectedRegionIds.length > 0) {
    return [...selectedRegionIds];
  }

  return regions.map((region) => region.id);
}

/**
 * Regions the header driver counts (free / busy / total) are computed for.
 *
 * A region picked in the map's own Regions dropdown wins, so the counts always describe the drivers shown on the
 * map. With nothing picked there (or the "no filter" entry) the counts keep following the toolbar selection.
 */
export function resolvePartnerCountRegionIds(
  mapSelectedRegionIds: number[],
  toolbarEffectiveRegionIds: number[]
): number[] {
  return hasNoFilterSelection(mapSelectedRegionIds) || mapSelectedRegionIds.length === 0
    ? toolbarEffectiveRegionIds
    : mapSelectedRegionIds;
}

export function deriveEffectiveCarTypeIds(
  carTypes: CarTypeRecord[],
  selectedCarTypeIds: number[]
): number[] {
  const effectiveSelectedCarTypeIds = hasNoFilterSelection(selectedCarTypeIds)
    ? []
    : selectedCarTypeIds.filter((carTypeId) => carTypeId !== 0);

  if (effectiveSelectedCarTypeIds.length > 0) {
    return [...effectiveSelectedCarTypeIds];
  }

  return carTypes
    .map((carType) => carType.id)
    .filter((carTypeId) => carTypeId !== 0);
}

export function getCountryDefinition(code: CountryCode): CountryDefinition {
  return LEGACY_COUNTRY_DEFINITIONS.find((country) => country.code === code) ?? LEGACY_COUNTRY_DEFINITIONS[0];
}

export function matchesOrderToSelectedTariffs(
  order: Pick<OrderRecord, 'tariffId'> | null | undefined,
  selectedTariffIds: number[]
): boolean {
  if (!order?.tariffId) {
    return false;
  }

  if (hasNoFilterSelection(selectedTariffIds)) {
    return true;
  }

  return selectedTariffIds.includes(order.tariffId);
}

export function hasNoFilterSelection(selectedIds: number[]): boolean {
  return selectedIds.includes(NO_FILTER_SELECTION_ID);
}

type DriverStaticEnrichment = Pick<
  EnrichedDriverRecord,
  | 'regionIds'
  | 'activeRegionIds'
  | 'carTypeIds'
  | 'activeCarTypeIds'
  | 'primaryCarTypeId'
  | 'isTruck'
  | 'isManipulator'
  | 'tariffRegionPairs'
  | 'optionNames'
  | 'activeOptionNames'
>;

export function enrichDriverRecord(
  driver: DriverRecord,
  tariffLookup: Record<number, TariffRecord>,
  optionLookup: Record<number, OptionRecord>,
  airConditioningDriverIds: ReadonlySet<string>
): EnrichedDriverRecord {
  return assembleEnrichedDriver(
    driver,
    computeDriverStaticEnrichment(driver, tariffLookup, optionLookup),
    airConditioningDriverIds
  );
}

/**
 * Same result as enrichDriverRecord, but the part that only depends on a driver's tariff/option lists and the
 * lookup tables (regions, car types, truck/manipulator flags, grouped tariffs, option names) is remembered.
 *
 * Drivers are re-emitted on every location poll with a new object but the same tariff/option arrays (those only
 * change when the slow-changing /partners/details data is refreshed), so a location poll for thousands of drivers
 * rebuilds almost nothing. The cache is keyed by array identity and holds entries weakly.
 */
export function createCachedDriverEnricher(): typeof enrichDriverRecord {
  const cache = new WeakMap<
    DriverTariffAssignment[],
    {
      activeTariffIds: number[];
      optionAssignments: DriverOptionAssignment[];
      tariffLookup: Record<number, TariffRecord>;
      optionLookup: Record<number, OptionRecord>;
      value: DriverStaticEnrichment;
    }
  >();

  return (driver, tariffLookup, optionLookup, airConditioningDriverIds) => {
    const cached = cache.get(driver.tariffAssignments);
    let value: DriverStaticEnrichment;

    if (
      cached &&
      cached.activeTariffIds === driver.activeTariffIds &&
      cached.optionAssignments === driver.optionAssignments &&
      cached.tariffLookup === tariffLookup &&
      cached.optionLookup === optionLookup
    ) {
      value = cached.value;
    } else {
      value = computeDriverStaticEnrichment(driver, tariffLookup, optionLookup);
      cache.set(driver.tariffAssignments, {
        activeTariffIds: driver.activeTariffIds,
        optionAssignments: driver.optionAssignments,
        tariffLookup,
        optionLookup,
        value
      });
    }

    return assembleEnrichedDriver(driver, value, airConditioningDriverIds);
  };
}

function assembleEnrichedDriver(
  driver: DriverRecord,
  staticEnrichment: DriverStaticEnrichment,
  airConditioningDriverIds: ReadonlySet<string>
): EnrichedDriverRecord {
  return {
    ...driver,
    airConditioning:
      airConditioningDriverIds.has(driver.userId) ||
      (!!driver.driverId && airConditioningDriverIds.has(driver.driverId)),
    ...staticEnrichment
  };
}

function computeDriverStaticEnrichment(
  driver: DriverRecord,
  tariffLookup: Record<number, TariffRecord>,
  optionLookup: Record<number, OptionRecord>
): DriverStaticEnrichment {
  const tariffAssignments = [...driver.tariffAssignments];
  const activeTariffAssignments = tariffAssignments.filter((assignment) => assignment.active);
  const regionIds = uniqueNumbers(
    tariffAssignments
      .map((assignment) => tariffLookup[assignment.tariffId]?.regionId)
      .filter(isNumber)
  );
  const activeRegionIds = uniqueNumbers(
    activeTariffAssignments
      .map((assignment) => tariffLookup[assignment.tariffId]?.regionId)
      .filter(isNumber)
  );
  const carTypeIds = uniqueNumbers(
    tariffAssignments
      .map((assignment) => tariffLookup[assignment.tariffId]?.typeId)
      .filter(isNumber)
  );
  const activeCarTypeIds = uniqueNumbers(
    activeTariffAssignments
      .map((assignment) => tariffLookup[assignment.tariffId]?.typeId)
      .filter(isNumber)
  );
  const primaryTariffId =
    driver.activeTariffIds[0] ?? activeTariffAssignments[0]?.tariffId ?? tariffAssignments[0]?.tariffId;
  const primaryCarTypeId = (() => {
    const primaryType = tariffLookup[primaryTariffId]?.typeId;
    if (isNumber(primaryType)) return primaryType;
    for (const assignment of [...activeTariffAssignments, ...tariffAssignments]) {
      const t = tariffLookup[assignment.tariffId]?.typeId;
      if (isNumber(t)) return t;
    }
    return null;
  })();

  const isTruck = [...activeTariffAssignments, ...tariffAssignments].some((assignment) => {
    const tariff = tariffLookup[assignment.tariffId];
    return tariff && normalizeTariffName(tariff.name).startsWith(TRUCK_TARIFF_NAME_PREFIX);
  });

  const isManipulator = [...activeTariffAssignments, ...tariffAssignments].some((assignment) => {
    const tariff = tariffLookup[assignment.tariffId];
    return tariff && normalizeTariffName(tariff.name).startsWith(MANIPULATOR_TARIFF_NAME_PREFIX);
  });

  return {
    regionIds,
    activeRegionIds,
    carTypeIds,
    activeCarTypeIds,
    primaryCarTypeId,
    isTruck,
    isManipulator,
    tariffRegionPairs: buildTariffRegionPairs(tariffAssignments, tariffLookup),
    optionNames: buildOptionNames(driver.optionAssignments, optionLookup),
    activeOptionNames: buildOptionNames(
      driver.optionAssignments.filter((assignment) => assignment.active),
      optionLookup
    )
  };
}

function buildTariffRegionPairs(
  assignments: DriverTariffAssignment[],
  tariffLookup: Record<number, TariffRecord>
): DriverTariffRegionGroup[] {
  const regionGroups = new Map<number, DriverTariffRegionGroup>();
  const unknownTariffs: DriverTariffAssignment[] = [];

  for (const assignment of assignments) {
    const tariff = tariffLookup[assignment.tariffId];

    if (!isNumber(tariff?.regionId)) {
      unknownTariffs.push(assignment);
      continue;
    }

    const existing = regionGroups.get(tariff.regionId);

    if (existing) {
      existing.tariffs.push(assignment);
      continue;
    }

    regionGroups.set(tariff.regionId, {
      regionId: tariff.regionId,
      tariffs: [assignment]
    });
  }

  const groups = [...regionGroups.values()].sort((left, right) => {
    const leftRegionId = left.regionId ?? Number.MAX_SAFE_INTEGER;
    const rightRegionId = right.regionId ?? Number.MAX_SAFE_INTEGER;
    return leftRegionId - rightRegionId;
  });

  if (unknownTariffs.length > 0) {
    groups.push({
      regionId: null,
      tariffs: unknownTariffs
    });
  }

  return groups;
}

function buildOptionNames(
  assignments: DriverOptionAssignment[],
  optionLookup: Record<number, OptionRecord>
): string[] {
  return assignments
    .map((assignment) => optionLookup[assignment.optionId]?.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0);
}

function uniqueNumbers(values: number[]): number[] {
  return [...new Set(values)];
}

function isYerevanRegion(region: RegionRecord): boolean {
  return region.name.trim().toLocaleLowerCase() === 'yerevan';
}

function readNumber(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function isNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function normalizeTariffName(value: string): string {
  return value.trim().toLowerCase();
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}
